/**
 * Marketing Flex Table — the user's remembered setup (R28, KTD8).
 *
 * One value is stored: dimensions, view, metrics in order, sort, period and
 * filters. Expand state and the search box are not — they describe a moment,
 * not a setup.
 *
 * A PRESET IS STORED AS ITS KEY and re-resolved to today's dates on load, so
 * "Last 365 days" saved last month still means the last 365 days. A custom
 * range has nothing to re-resolve and restores its stored dates.
 *
 * Loading must never block the page: a failed or unreadable load falls back to
 * the defaults. Nothing is saved until the load has succeeded, so neither the
 * defaults shown while it is in flight nor the ones shown after it failed ever
 * overwrite what the user had. After a failed load, changes on this visit are
 * not remembered.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { DATE_RANGE_PRESETS, resolvePreset, type DateRangePresetKey } from '@/lib/dateRangePresets';
import { loadSetup, saveSetup, type FlexFilterSelection } from '@/services/marketingFlexTable.service';
import { DEFAULT_METRICS, DIMENSIONS, METRICS, type DimensionKey, type MetricKey } from './flexCatalog';
import type { FlexSetup, FlexSort, FlexView } from './pivot';

/** Unbounded or future-facing presets make no sense for an activity report (R4). */
const EXCLUDED_PRESETS: DateRangePresetKey[] = ['all', 'now', 'next90days'];

export const FLEX_PRESETS = DATE_RANGE_PRESETS.filter((p) => !EXCLUDED_PRESETS.includes(p.key));

export const DEFAULT_PRESET: DateRangePresetKey = '365days';

export const DEFAULT_PRIMARY: DimensionKey = 'source';

const SAVE_DEBOUNCE_MS = 800;

export interface FlexPeriod {
  /** null = a custom range. */
  presetKey: DateRangePresetKey | null;
  from: string;
  to: string;
}

export interface FlexPageSetup extends FlexSetup {
  period: FlexPeriod;
  filters: FlexFilterSelection;
}

export function presetPeriod(key: DateRangePresetKey, now: Date = new Date()): FlexPeriod {
  const { from, to } = resolvePreset(key, now);
  return { presetKey: key, from, to };
}

export function defaultFlexSetup(now: Date = new Date()): FlexPageSetup {
  return {
    primary: DEFAULT_PRIMARY,
    secondary: null,
    view: 'nested',
    metrics: [...DEFAULT_METRICS],
    sort: null,
    period: presetPeriod(DEFAULT_PRESET, now),
    filters: { facility: [], tags: [], patientType: [] },
  };
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isDimension = (v: unknown): v is DimensionKey => typeof v === 'string' && Object.prototype.hasOwnProperty.call(DIMENSIONS, v);
const isMetric = (v: unknown): v is MetricKey => typeof v === 'string' && Object.prototype.hasOwnProperty.call(METRICS, v);
const isDay = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)).filter((x) => x !== '') : [];
}

/**
 * The metric catalog a stored setup was saved against. A setup from an older
 * catalog gets that catalog's new metrics added once (docs/plans/2026-11-02-002
 * R8); every save then records the current version, so a metric the user
 * removes afterwards stays removed.
 */
export const CATALOG_VERSION = 2;

/** Metrics added in each catalog version, in default order. */
const ADDED_IN: Record<number, MetricKey[]> = {
  2: ['newCustomers', 'customerRate', 'revenuePerNewCustomer'],
};

/** Insert each missing metric beside its nearest default-order neighbour already in the list. */
function addMetrics(list: MetricKey[], added: MetricKey[]): MetricKey[] {
  const out = [...list];
  for (const metric of added) {
    if (out.includes(metric)) continue;
    const at = DEFAULT_METRICS.indexOf(metric);
    const before = DEFAULT_METRICS.slice(0, at).reverse().find((m) => out.includes(m));
    const after = DEFAULT_METRICS.slice(at + 1).find((m) => out.includes(m));
    if (before !== undefined) out.splice(out.indexOf(before) + 1, 0, metric);
    else if (after !== undefined) out.splice(out.indexOf(after), 0, metric);
    else out.push(metric);
  }
  return out;
}

/**
 * The stored value made safe: unknown dimension and metric keys are dropped
 * silently, and anything unreadable takes its default. `migrated` says the
 * metrics were brought up to the current catalog, so the result should be
 * saved back.
 */
export function readStoredSetup(raw: unknown, now: Date = new Date()): { setup: FlexPageSetup; migrated: boolean } {
  const base = defaultFlexSetup(now);
  if (!isRecord(raw)) return { setup: base, migrated: false };

  const primary = raw.primary === null ? null : isDimension(raw.primary) ? raw.primary : base.primary;
  const secondary = primary !== null && isDimension(raw.secondary) && raw.secondary !== primary ? raw.secondary : null;
  const view: FlexView = raw.view === 'side-by-side' ? 'side-by-side' : 'nested';

  let metrics = Array.isArray(raw.metrics)
    ? raw.metrics.filter(isMetric).filter((m, i, all) => all.indexOf(m) === i)
    : base.metrics;

  let migrated = false;
  const version = typeof raw.catalogVersion === 'number' ? raw.catalogVersion : 1;
  for (let v = version + 1; v <= CATALOG_VERSION; v++) {
    const next = addMetrics(metrics, ADDED_IN[v] ?? []);
    migrated = migrated || next.length !== metrics.length;
    metrics = next;
  }

  let sort: FlexSort | null = null;
  if (isRecord(raw.sort) && (raw.sort.dir === 'asc' || raw.sort.dir === 'desc')) {
    const by = raw.sort.by;
    if (by === 'dimension' || (isMetric(by) && metrics.includes(by))) sort = { by, dir: raw.sort.dir };
  }

  let period = base.period;
  if (isRecord(raw.period)) {
    const key = raw.period.presetKey;
    if (typeof key === 'string' && FLEX_PRESETS.some((p) => p.key === key)) {
      period = presetPeriod(key as DateRangePresetKey, now);
    } else if (key === null && isDay(raw.period.from) && isDay(raw.period.to)) {
      period = { presetKey: null, from: raw.period.from, to: raw.period.to };
    }
  }

  const filters = isRecord(raw.filters)
    ? {
        facility: stringList(raw.filters.facility),
        tags: stringList(raw.filters.tags),
        patientType: stringList(raw.filters.patientType).filter((v) => v === '1' || v === '2'),
      }
    : base.filters;

  return { setup: { primary, secondary, view, metrics, sort, period, filters }, migrated };
}

/** What is written: the period as a key when it is a preset, dates always. */
function toStored(setup: FlexPageSetup) {
  return {
    catalogVersion: CATALOG_VERSION,
    primary: setup.primary,
    secondary: setup.secondary,
    view: setup.view,
    metrics: setup.metrics,
    sort: setup.sort,
    period: { presetKey: setup.period.presetKey, from: setup.period.from, to: setup.period.to },
    filters: setup.filters,
  };
}

export function useFlexSetup() {
  const [setup, setSetup] = useState<FlexPageSetup>(() => defaultFlexSetup());
  const [ready, setReady] = useState(false);
  // Only a successful load turns saving on; see the docblock.
  const [canSave, setCanSave] = useState(false);
  const lastSaved = useRef<string | null>(null);
  const pending = useRef<string | null>(null);
  // Set by the first change the user makes, so a saved setup that arrives
  // after it does not undo what they just did.
  const touched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadSetup()
      .then((raw) => {
        if (cancelled) return;
        const { setup: restored, migrated } = raw === null ? { setup: defaultFlexSetup(), migrated: false } : readStoredSetup(raw);
        // A migrated setup has not been saved in its new form; leaving
        // lastSaved empty lets the save effect write it once.
        lastSaved.current = migrated ? null : JSON.stringify(toStored(restored));
        if (!touched.current) setSetup(restored);
        setCanSave(true);
      })
      .catch(() => {
        // Falls back to the defaults already showing; the page must still work.
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canSave) return undefined;
    const serialized = JSON.stringify(toStored(setup));
    if (serialized === lastSaved.current) {
      // Changed back to what is already saved: nothing is left to flush on unmount.
      pending.current = null;
      return undefined;
    }
    pending.current = serialized;
    const timer = setTimeout(() => {
      pending.current = null;
      lastSaved.current = serialized;
      saveSetup(JSON.parse(serialized)).catch(() => {
        // Remembering the setup is a convenience; a failed save changes nothing on screen.
      });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [setup, canSave]);

  // Leaving the page inside the debounce window still keeps the last change.
  useEffect(
    () => () => {
      if (pending.current !== null) {
        saveSetup(JSON.parse(pending.current)).catch(() => {});
        pending.current = null;
      }
    },
    [],
  );

  const update = useCallback((patch: Partial<FlexPageSetup> | ((prev: FlexPageSetup) => FlexPageSetup)) => {
    touched.current = true;
    setSetup((prev) => (typeof patch === 'function' ? patch(prev) : { ...prev, ...patch }));
  }, []);

  return { setup, update, ready };
}
